import classNames from 'classnames';
import { motion } from 'framer-motion';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from '../Icon';

export enum ButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  tertiary = 'tertiary',
  primaryWhite = 'primary-white',
  secondaryWhite = 'secondary-white',
  secondaryIndigo = 'secondary-indigo',
  disabled = 'disabled',
  disabledIndigo = 'disabledIndigo',
  indigoLight = 'indigo-light',
  indigoDark = 'indigo-dark',
  indigoDark2 = 'indigo-dark-2',
}

export enum ButtonSize {
  default = 'default',
  large = 'large',
  xlarge = 'xlarge',
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The buttons variant
   */
  variant?: ButtonVariant;
  /**
   * The size of the button
   */
  size?: ButtonSize;
  /**
   * The text to appear in the middle of the button
   */
  text?: string;

  children?: any;
  icon?: any;
}

export type Ref = HTMLButtonElement;

const Button = React.forwardRef<Ref, ButtonProps>((props, ref) => {
  const {
    variant,
    size = ButtonSize.default,
    text,
    onClick,
    className,
    children,
    icon,
    ...rest
  } = props;

  return (
    <button
      ref={ref}
      {...rest}
      className={twMerge(
        classNames(
          'relative flex flex-row items-center justify-center border rounded-lg whitespace-nowrap transition-colors',
          {
            'px-4 py-2':
              size === ButtonSize.default && variant !== ButtonVariant.tertiary,
            'px-6 py-3':
              size === ButtonSize.large && variant !== ButtonVariant.tertiary,
            'text-lg font-semibold': size === ButtonSize.large,
            'text-7xl font-semibold px-10 py-6': size === ButtonSize.xlarge,
            'text-base font-semibold': size === ButtonSize.default,
            [[
              'text-white',
              'bg-indigo-1',
              'hover:bg-indigo-2',
              'border-indigo-1',
              'active:bg-indigo-3-dark',
              'disabled:bg-gray-4-light',
              'disabled:text-gray-2-light',
              'disabled:border-gray-4-light',
            ].join(' ')]: variant === ButtonVariant.primary,
            [[
              'bg-white',
              'text-gray-3-dark',
              'border-gray-3-light',
              'hover:border-gray-2-light',
              'active:bg-gray-6-light',
              'active:border-gray-1',
              'disabled:border-gray-4-light',
              'disabled:text-gray-3-light',
            ].join(' ')]: variant === ButtonVariant.secondary,
            [[
              'p-0',
              'bg-transparent',
              'border-transparent',
              'text-indigo-1',
              'hover:text-indigo-2',
              'active:text-indigo-3-dark',
              'disabled:text-gray-3-light',
            ].join(' ')]: variant === ButtonVariant.tertiary,
            [[
              'bg-white',
              'text-indigo-1',
              'border-transparent',
              'hover:bg-indigo-2-light',
              'active:bg-purple-active-button',
            ].join(' ')]: variant === ButtonVariant.primaryWhite,
            [[
              'bg-transparent',
              'text-white',
              'hover:text-indigo-2-light',
              'hover:border-indigo-2-light',
              'active:text-purple-active-button',
              'active:border-purple-active-button',
            ].join(' ')]: variant === ButtonVariant.secondaryWhite,
            [[
              'bg-transparent',
              'text-indigo-1',
              'border-indigo-1',
              'hover:text-indigo-2-light',
              'border-2',
              'hover:border-indigo-2-light',
              'active:text-purple-active-button',
              'active:border-purple-active-button',
            ].join(' ')]: variant === ButtonVariant.secondaryIndigo,
            [[
              'cursor-not-allowed',
              'text-gray-2-light',
              'bg-gray-4-light',
              'hover:bg-gray-4-light',
              'border-gray-4-light',
              'disabled:bg-gray-4-light',
              'disabled:text-gray-2-light',
              'disabled:border-gray-4-light',
            ].join(' ')]: variant === ButtonVariant.disabled,
            [[
              'cursor-not-allowed',
              'text-white',
              'bg-indigo-2-light',
              'hover:bg-indigo-2-light',
              'border-indigo-2-light',
              'disabled:bg-indigo-2-light',
              'disabled:text-indigo-2-light',
              'disabled:border-indigo-2-light',
            ].join(' ')]: variant === ButtonVariant.disabledIndigo,
            [[
              'bg-indigo-3-light',
              'text-indigo-1',
              'border-indigo-1',
              'hover:text-indigo-3-dark',
              'border-2',
              'hover:border-indigo-2-light',
              'active:text-indigo-3-dark',
              'active:bg-indigo-2-light',
              'active:border-purple-active-button',
            ].join(' ')]: variant === ButtonVariant.indigoLight,
            [[
              'text-white',
              'bg-indigo-3-dark',
              'hover:bg-indigo-1',
              'hover:border-indigo-1',
              'border-indigo-3-dark',
              'active:bg-indigo-3-dark',
              'disabled:bg-gray-4-light',
              'disabled:text-gray-2-light',
              'disabled:border-gray-4-light',
            ].join(' ')]: variant === ButtonVariant.indigoDark,
            [[
              'text-white',
              'bg-indigo-2',
              'hover:bg-gradient-to-r hover:from-purple-2-dark hover:via-purple-3-dark hover:purple-3-dark ',
              'hover:border-indigo-1',
              'border-indigo-2',
              'active:bg-indigo-3-dark',
              'disabled:bg-gray-4-light',
              'disabled:text-gray-2-light',
              'disabled:border-gray-4-light',
            ].join(' ')]: variant === ButtonVariant.indigoDark2,
          },
          className
        )
      )}
      onClick={onClick}
      style={{ outline: 'none' }}
    >
      {icon ? <Icon className="mr-2" iconType={icon} /> : ''}

      {text ? text : children}
    </button>
  );
});

Button.displayName = 'Button';

export default React.memo(motion(Button));
